// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { memo } from "react";
import { MdCheck, MdClose } from "react-icons/md";
import { AiOutlineDown } from "react-icons/ai";
import { LuSearch } from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useRef, useMemo } from "react";
import { userLogsAPI } from "../../services/session.service";
import {
  getTicketFeatureAPI,
  getTcketTypeAPI,
} from "../../services/ticket.service";
import { invokeSuccess } from "../../store/auth/action";
import SuccessPop from "../popups/SuccessPop";
import { TriangleAlertIcon, DatabaseIcon, CircleHelpIcon, ChevronRightIcon } from "../icons";

import StartLoader from "../loaders/StartLoader";
import { listTickets, raiseTicket } from "../../services/ticket.service";

import { getSession } from "../../utils/sessionstore";
import ViewTicketSection from "../sections/ViewTicketSection";
import { logAmplitudeEvent } from "../../utils/amplitude/functions";
import amplitude from "../../utils/amplitude/initiate";

// Same 3 impact options as the sidepanel design - the stored `priority` value is
// now the label text itself (matches CustomTicket.model.js's enum), replacing the
// old High/Medium/Low values.
const IMPACT_OPTIONS = [
  { key: "blocked", label: "Cannot Proceed", icon: DatabaseIcon, priority: "Cannot Proceed" },
  { key: "slowed", label: "Slowed me down", icon: TriangleAlertIcon, priority: "Slowed me down" },
  { key: "note", label: "Just a note (FYI)", icon: CircleHelpIcon, priority: "Just a note (FYI)" },
];

// One category row: label + expand chevron, revealing its dashboards underneath
// when open. Reused for both top-level categories and the ones nested a level
// deeper under the synthetic "Network Intel" group.
const CategoryRow = ({ cat, indent, isOpen, onToggle, onSelectDashboard }) => (
  <div>
    <button
      type="button"
      onClick={onToggle}
      className={`self-stretch w-full ${indent ? "pl-4 pr-2.5" : "px-2.5"} py-1.5 flex justify-between items-center gap-2 text-left hover:bg-general-accent ani`}
    >
      <span className="flex-1 min-w-0 truncate text-general-foreground text-sm font-normal leading-5">
        {cat.label || cat.categoryname}
      </span>
      <ChevronRightIcon className={`size-3.5 shrink-0 text-general-muted-foreground ani ${isOpen ? "rotate-90" : ""}`} />
    </button>
    {isOpen && (
      <div>
        {cat.dashboardIds.map((d) => (
          <div
            key={d._id || d.dashboardname}
            onClick={() => onSelectDashboard(d.dashboardname)}
            className={`${indent ? "pl-6" : "pl-4"} pr-2.5 py-1.5 text-left text-sm font-normal leading-5 truncate cursor-pointer hover:bg-general-accent ani`}
          >
            {d.dashboardname}
          </div>
        ))}
      </div>
    )}
  </div>
);

// Locally-styled replacement for the shared TicketDropDown (kept untouched since it's
// used elsewhere) - same search + collapsible-category layout as the sidebar's
// dashboard search flyout, flattened from subscriptions -> categories -> dashboards.
// Same "Lobby Page" fallback, same setCheck/check contract as before. Pre-filled from
// the current report/page (see the auto-select effect below), but still editable via
// the dropdown list in case the user wants to pick a different one.
const DashboardNameField = ({ options, check, setCheck, showwarning, setShowWarning }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedCats, setExpandedCats] = useState({});
  const ref = useRef(null);

  const categories = useMemo(() => {
    const term = search.trim().toLowerCase();
    const flat = (options || [])
      .flatMap((sub) => sub.productcategoryIds || [])
      .map((cat) => ({
        ...cat,
        dashboardIds: (cat.dashboardIds || []).filter(
          (d) => d?.dashboardname && (!term || d.dashboardname.toLowerCase().includes(term))
        ),
      }))
      .filter((cat) => cat.dashboardIds.length > 0);

    // "Network Intelligence ..." categories are shown as sub-items nested under one
    // "Network Intel" parent, instead of as their own flat top-level rows - which
    // dashboards fall under each of them is untouched, still driven by the same
    // dashboardIds data above.
    const isNetworkIntel = (cat) => (cat.label || cat.categoryname || "").trim().toLowerCase().startsWith("network intelligence");
    const firstNetworkIndex = flat.findIndex(isNetworkIntel);
    if (firstNetworkIndex === -1) return flat;

    const networkChildren = flat.filter(isNetworkIntel);
    const rest = flat.filter((cat) => !isNetworkIntel(cat));
    rest.splice(firstNetworkIndex, 0, {
      _id: "network-intel-group",
      label: "Network Intel",
      isGroup: true,
      children: networkChildren,
    });
    return rest;
  }, [options, search]);

  // Auto-expand whichever category holds the currently selected dashboard, so
  // opening the dropdown shows it in context instead of fully collapsed.
  useEffect(() => {
    if (!open) return;
    const holds = (cat) => cat.dashboardIds?.some((d) => d.dashboardname === check);
    const keysToExpand = [];
    for (const cat of categories) {
      if (cat.isGroup) {
        const child = cat.children.find(holds);
        if (child) {
          keysToExpand.push(cat._id, child._id || child.categoryname || child.label);
        }
      } else if (holds(cat)) {
        keysToExpand.push(cat._id || cat.categoryname || cat.label);
      }
    }
    if (keysToExpand.length > 0) {
      setExpandedCats((prev) => {
        const next = { ...prev };
        keysToExpand.forEach((key) => { next[key] = true; });
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectOption = (name) => {
    setCheck(name);
    setShowWarning(false);
    setOpen(false);
    setSearch("");
  };

  const toggleCategory = (key) => {
    setExpandedCats((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const showLobbyPage = !search.trim() || "lobby page".includes(search.trim().toLowerCase());

  return (
    <div className="relative self-stretch" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="self-stretch w-full min-h-8 pl-2.5 pr-7 py-1.5 bg-general-background rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-general-border flex justify-between items-center gap-2 ani"
      >
        <span className="flex-1 min-w-0 truncate text-left text-general-foreground text-xs font-normal leading-4">
          {check && check !== "-Select-" ? check : "-Select-"}
        </span>
        <AiOutlineDown className={`shrink-0 size-2.5 text-general-muted-foreground ani ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-10 bg-general-background rounded-lg shadow-[0px_4px_12px_0px_rgba(0,0,0,0.12)] outline outline-1 outline-offset-[-1px] outline-general-border overflow-hidden flex flex-col">
          <div className="shrink-0 px-2.5 py-2 flex justify-start items-center gap-2 border-b border-general-border">
            <LuSearch className="size-3.5 shrink-0 text-general-muted-foreground" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="flex-1 min-w-0 bg-transparent text-general-foreground placeholder:text-general-muted-foreground text-xs font-normal leading-4 outline-none"
            />
          </div>

          <div className="max-h-48 overflow-y-auto py-1">
            {categories.length === 0 && !showLobbyPage && (
              <div className="px-2.5 py-1.5 text-left text-general-muted-foreground text-xs font-normal leading-4">
                No dashboards found
              </div>
            )}
            {categories.map((cat) => {
              const key = cat._id || cat.categoryname || cat.label;
              const isCatOpen = Boolean(search.trim()) || Boolean(expandedCats[key]);

              if (cat.isGroup) {
                return (
                  <div key={key}>
                    <button
                      type="button"
                      onClick={() => toggleCategory(key)}
                      className="self-stretch w-full px-2.5 py-1.5 flex justify-between items-center gap-2 text-left hover:bg-general-accent ani"
                    >
                      <span className="flex-1 min-w-0 truncate text-general-foreground text-sm font-normal leading-5">
                        {cat.label}
                      </span>
                      <ChevronRightIcon
                        className={`size-3.5 shrink-0 text-general-muted-foreground ani ${isCatOpen ? "rotate-90" : ""}`}
                      />
                    </button>
                    {isCatOpen && (
                      <div>
                        {cat.children.map((child) => {
                          const childKey = child._id || child.categoryname || child.label;
                          const isChildOpen = Boolean(search.trim()) || Boolean(expandedCats[childKey]);
                          return (
                            <CategoryRow
                              key={childKey}
                              cat={child}
                              indent
                              isOpen={isChildOpen}
                              onToggle={() => toggleCategory(childKey)}
                              onSelectDashboard={selectOption}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <CategoryRow
                  key={key}
                  cat={cat}
                  isOpen={isCatOpen}
                  onToggle={() => toggleCategory(key)}
                  onSelectDashboard={selectOption}
                />
              );
            })}
            {showLobbyPage && (
              <div
                onClick={() => selectOption("Lobby Page")}
                className="px-2.5 py-1.5 text-left text-sm font-normal leading-5 truncate cursor-pointer hover:bg-general-accent ani"
              >
                Lobby Page
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Locally-styled replacement for the shared ButtonOptionSelector - same impact-pill
// design as the sidepanel, wired so picking one sets priority = {name: "Low"|"Medium"|"High"}.
const PriorityField = ({ checked, setChecked, setShowWarning }) => (
  <div className="self-stretch inline-flex justify-start items-center gap-1.5 flex-wrap content-center">
    {IMPACT_OPTIONS.map((option) => {
      const Icon = option.icon;
      const selected = checked?.name === option.priority;
      return (
        <button
          key={option.key}
          type="button"
          onClick={() => {
            setChecked({ name: option.priority });
            setShowWarning(false);
          }}
          className={`min-h-7 px-1.5 py-1 rounded-md outline outline-1 outline-offset-[-1px] flex justify-center items-center gap-1 text-xs font-normal leading-4 text-general-foreground ani hover:shadow-[0px_0px_0px_3px_rgba(212,212,212,1.00)] ${
            selected ? "outline-general-primary bg-general-accent" : "outline-general-border"
          }`}
        >
          <Icon className="size-3.5" />
          {option.label}
        </button>
      );
    })}
  </div>
);

const CreateTicketPopup = ({ searchedResult, setSearchedResult, closePopup, variant = "modal", onViewHistory }) => {
  const { subscriptions, success, isLoading, userDetails, clientname, orgs, activedashboard } = useSelector(
    (state) => ({
      subscriptions: state.subscriptions,
      success: state.success,
      isLoading: state.isLoading,
      userDetails: state.userDetails,
      clientname: state.clientname,
      orgs: state.orgs,
      activedashboard: state.activedashboard,
    })
  );

  const sessionId = getSession("session_id");
  const token = getSession("session_hwai");
  const clientid = getSession("client_id");

  async function callUserLogAPI() {
    try {
      const logresponse = userLogsAPI(
        "TICKET",
        "Ticket",
        userDetails,
        clientname,
        "Ticket",
        "Viewed"
      );
      // console.log(logresponse, "banner click");
    } catch (err) {
      console.log(err.message);
    }
  }

  async function populateTicketVeiw() {
    const response = await listTickets();
    setSearchedResult(response.tickets);
  }

  const [selectedFeature, setSelectedFeature] = useState("");
  const [currentView, setCurrentView] = useState("create");

  const [feature, setFeature] = useState([]);
  const [detail, setDetail] = useState("");
  const [priority, setPriority] = useState({});

  const [featureWarning, setFeatureWarning] = useState(false);
  const [detailWarning, setDetailWarning] = useState(false);
  const [priorityWarning, setPriorityWarning] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { clientID } = useParams();

  const handleViewHistory = async () => {
    logAmplitudeEvent(amplitude, "Ticket History", {
      eventTimestamp:new Date().toLocaleString('en-US', { timeZone: 'UTC' })
    });
    if (onViewHistory) {
      onViewHistory();
    } else {
      setCurrentView("viewHistory");
    }
  };

  const handleGoBack = () => {
    setCurrentView("create");
  };


  async function populateFeature() {
    setFeature(subscriptions);
  }
  useEffect(() => {
    populateFeature();

    callUserLogAPI();

    // Pre-fill the Dashboard Name field with whatever dashboard the user was on
    // when they opened this form, so they don't have to re-pick it manually.
    // On the Landing Page (no dashboard open), default it to "Lobby Page" instead.
    const currentPath = (clientID ? location.pathname.replace(new RegExp(`^/${clientID}`), "") : location.pathname) || "/";
    if (activedashboard?.dashboardname) {
      setSelectedFeature(activedashboard.dashboardname);
    } else if (currentPath === "/") {
      setSelectedFeature("Lobby Page");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        dispatch({ type: "SET_SUCCESS", payload: false });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);

  async function saveDetails() {
    try {
      const token = getSession("session_hwai");
      if (!token) {
        navigate("/login");
      } else {
        // Validation
        if (
          !priority ||
          !priority?.name ||
          !detail.length > 0 ||
          !selectedFeature ||
          Object.keys(selectedFeature).length === 0 ||
          selectedFeature?.includes("-Select-")
        ) {
          if (!priority || !priority?.name) setPriorityWarning(true);
          if (!detail.length > 0) setDetailWarning(true);
          if (
            !selectedFeature ||
            Object.keys(selectedFeature).length === 0 ||
            selectedFeature?.includes("-Select-")
          ) setFeatureWarning(true);
          return;
        }

        const payload = {
          feature: selectedFeature,
          detail: detail,
          priority: priority?.name,
          userId: token,
          clientId: clientid,
        };

        // Reset success/loading states. The global isLoading/success flags are also
        // watched by other already-mounted pages (e.g. the dashboard behind this
        // panel) - only dispatch them for the full-screen modal, where that's the
        // only loading indicator available. The panel has its own local one.
        setSubmitting(true);
        if (variant !== "panel") {
          dispatch({ type: "SET_LOADING", payload: true });
          dispatch({ type: "SET_SUCCESS", payload: false });
        }

        const response = await raiseTicket(payload);

        console.log("ticket raise success", response);
        logAmplitudeEvent(amplitude, "Ticket Raised", {
          clientId: clientid,
          clientName: clientname,
          ticketId: response._id,
          ticketPriority: response.priority,
          dashboardName: response.feature,
          ticketComments: response.detail,
          triggerType: "TICKET_POPUP",
          targetType: "SUCCESS",
          eventTimestamp:new Date().toLocaleString('en-US', { timeZone: 'UTC' })
        })


        // Clear form inputs immediately after successful submission
        setSelectedFeature("-Select-");
        setDetail("");
        setPriority({});
        populateTicketVeiw();

        // Ensure UI reflects success and no longer loading
        if (variant !== "panel") {
          dispatch({ type: "SET_SUCCESS", payload: true });
          dispatch({ type: "SET_LOADING", payload: false });
        }

        setTicketId(response?.customTicketId || response?.ticketId || response?.ticketNumber || response?._id || "");
        setSubmitted(true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
      if (variant !== "panel") {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    }
  }

  const formBody = (
    <>
      {currentView == "create" && submitted && (
        <div className="self-stretch flex-1 min-h-0 flex flex-col justify-start items-start">
          <div className="self-stretch flex-1 min-h-0 px-6 flex flex-col justify-center items-center gap-4 overflow-y-auto">
            <div className="self-stretch flex flex-col justify-start items-center gap-3">
              <div className="size-10 p-2 bg-green-50 rounded-full flex flex-col justify-center items-center overflow-hidden">
                <MdCheck className="size-5 text-success" />
              </div>
              <div className="self-stretch flex flex-col justify-start items-center gap-0.5">
                <div className="self-stretch text-center text-general-foreground text-sm font-medium leading-5">
                  Ticket created
                </div>
                <div className="self-stretch text-center text-general-muted-foreground text-xs font-normal leading-4">
                  Our team will get back to you
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleViewHistory}
              className="text-center text-general-secondary-foreground text-xs font-normal leading-4 hover:underline ani"
            >
              View My Ticket
            </button>
          </div>

          <div className="self-stretch mt-auto h-px bg-general-border" />

          <div className="self-stretch p-3">
            <button
              type="button"
              onClick={() => setSubmitted(false)}
              className="w-full min-h-8 px-3 py-1.5 bg-general-primary rounded-lg flex justify-center items-center gap-2 ani hover:brightness-90"
            >
              <span className="text-center text-general-primary-foreground text-xs font-medium leading-4">Done</span>
            </button>
          </div>
        </div>
      )}

      {currentView == "create" && !submitted && (
        <div className="self-stretch flex-1 min-h-0 flex flex-col justify-start items-start">
          <div className="self-stretch flex-1 min-h-0 p-3 flex flex-col justify-start items-start gap-3.5 overflow-y-auto">
            <div className="self-stretch flex flex-col justify-start items-start gap-1">
              <div className="text-general-foreground text-xs font-normal leading-4">Dashboard Name</div>
              <DashboardNameField
                options={feature}
                check={selectedFeature}
                setCheck={setSelectedFeature}
                showwarning={featureWarning}
                setShowWarning={setFeatureWarning}
              />
              {featureWarning && (
                <span className="text-left text-unofficial-destructive-text text-[11px] font-normal leading-4">
                  Required field missing
                </span>
              )}
            </div>

            <div className="self-stretch flex flex-col justify-center items-start gap-1.5">
              <div className="origin-left scale-75 -mb-1 text-xs font-normal leading-4 whitespace-nowrap">
                <span className="text-general-foreground">How much is this affecting you? </span>
                <span className="text-general-muted-foreground">(optional)</span>
              </div>
              <PriorityField checked={priority} setChecked={setPriority} setShowWarning={setPriorityWarning} />
              {priorityWarning && (
                <span className="text-left text-unofficial-destructive-text text-[11px] font-normal leading-4">
                  Required field missing
                </span>
              )}
            </div>

            <div className="self-stretch flex flex-col justify-start items-start gap-1 overflow-hidden">
              <div className="text-general-foreground text-xs font-normal leading-4">Details</div>
              <div className={`self-stretch rounded-lg outline outline-1 outline-offset-[-1px] flex flex-col justify-start items-start overflow-hidden ani ${detailWarning ? "outline-unofficial-destructive-border" : "outline-general-border"}`}>
                <textarea
                  value={detail}
                  onChange={(e) => {
                    setDetail(e.target.value);
                    if (e.target.value.trim()) setDetailWarning(false);
                  }}
                  placeholder="Tell us what happened."
                  className="self-stretch h-24 p-2 bg-general-background shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] text-general-foreground placeholder:text-general-muted-foreground text-xs font-normal leading-4 resize-y ani"
                />
              </div>

              {detailWarning && (
                <span className="self-stretch text-left text-unofficial-destructive-text text-[11px] font-normal leading-4">
                  Required field missing
                </span>
              )}
            </div>
          </div>

          <div className="self-stretch mt-auto h-px bg-general-border" />

          <div className="self-stretch p-3">
            <button
              type="button"
              onClick={saveDetails}
              className={`w-full min-h-8 px-3 py-1.5 bg-general-primary rounded-lg flex justify-center items-center gap-2 ani ${
                detail.trim() ? "hover:brightness-90" : "opacity-50"
              }`}
            >
              <span className="text-center text-general-primary-foreground text-xs font-medium leading-4">Raise ticket</span>
            </button>
          </div>
        </div>
      )}
      {currentView === "viewHistory" && (
        <ViewTicketSection
          searchedResult={searchedResult}
          setSearchedResult={setSearchedResult}
          onGoBack={handleGoBack}
          isPopup={true}
          variant="panel"
        />
      )}
      {variant !== "panel" && success && <SuccessPop />}
      {variant === "panel" ? submitting && <StartLoader /> : isLoading && <StartLoader />}
    </>
  );

  if (variant === "panel") {
    return <div className="self-stretch flex-1 min-h-0 flex flex-col justify-start items-start">{formBody}</div>;
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center">
      <div className="font-geist relative bg-general-background w-[80%] max-w-[600px] rounded-xl shadow-lg outline outline-1 outline-general-border flex flex-col justify-start items-start overflow-hidden">
        <div className="self-stretch px-4 py-3 flex justify-between items-center border-b border-general-border">
          <div className="text-general-foreground text-base font-semibold leading-6">Create Ticket</div>
          <button type="button" onClick={closePopup} className="p-1.5 rounded-lg hover:bg-general-accent ani">
            <MdClose className="text-xl text-general-foreground" aria-label="Close Ticket Popup" />
          </button>
        </div>
        {formBody}
      </div>
    </div>
  );

};

export default memo(CreateTicketPopup);
